<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { activeDiscountAction } from '@/api/functions/discounts/active-discount-action'
import { inactiveDiscountAction } from '@/api/functions/discounts/inactive-discount-action'
import { useMutationApi } from '@/composables/useApi'
import { useJalaaliDate } from '@/composables/useJalaaliDate'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import { numberWithCommas } from '@/utils/number-with-commas'
import UpdateDiscountDetailsModal from './UpdateDiscountDetailsModal.vue'

const props = defineProps({
  discount: { type: Object },
  currencies: {
    type: Array,
  },
  discountTypeOptions: {
    type: Array,
  },
})

const emit = defineEmits(['confirmDiscount'])

const toast = useToast()
const router = useRouter()
const { toJalaali } = useJalaaliDate()
const showUpdateModal = ref(false)
const showInactiveModal = ref(false)
const showActiveModal = ref(false)

const discountId = computed(() => props?.discount?.id)

const formatDate = (dateString: string) => {
  if (!dateString) return ''
  const jDate = toJalaali(dateString)
  return `${jDate} `
}

const goToDiscountDetails = () => {
  if (discountId.value) {
    router.push({
      path: `/discounts/${discountId.value}`,
      query: { backTo: '/settings/discounts' },
    })
  }
}

// Inactive Discount Action
const inActiveMutation = useMutationApi({
  mutationFn: (id: string) => inactiveDiscountAction(id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'تخفیف با موفقیت غیرفعال شد')
    emit('confirmDiscount')
    showInactiveModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleInactiveDiscount = () => {
  if (!discountId.value) return
  inActiveMutation.mutate(props?.discount?.id)
}

// Active Discount Action
const activeMutation = useMutationApi({
  mutationFn: (id: string) => activeDiscountAction(id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'تخفیف با موفقیت فعال شد')
    emit('confirmDiscount')
    showActiveModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleActiveDiscount = () => {
  if (!props?.discount?.id) return
  activeMutation.mutate(props?.discount?.id)
}
</script>

<template>
    <div class="bg-white rounded-xl p-5 lg:p-8 lg:pb-6 relative flex flex-col-reverse lg:flex-col gap-6 lg:gap-0">
        <ConfirmModal v-model="showInactiveModal" title="غیرفعال کردن تخفیف"
            :message="'آیا از غیرفعال کردن تخفیف اطمینان دارید؟'" confirm-text="بله غیرفعال شود" cancel-text="بازگشت"
            @confirm="handleInactiveDiscount" @cancel="showInactiveModal = false" />

        <ConfirmModal v-model="showActiveModal" title="فعال کردن تخفیف"
            :message="'آیا از فعال کردن تخفیف اطمینان دارید؟'" confirm-text="بله فعال شود" cancel-text="بازگشت"
            @confirm="handleActiveDiscount" @cancel="showActiveModal = false" />

        <UpdateDiscountDetailsModal v-model="showUpdateModal" @cancel="showUpdateModal = false" :currencies="currencies"
            :discount="discount" :discountTypeOptions="discountTypeOptions" @confirm="emit('confirmDiscount')" />

        <div class="relative lg:absolute bottom-0 lg:top-8 lg:end-8">
            <div class=" w-full lg:w-fit flex  items-center">
                <Button @click="showUpdateModal = true" :isAdmin="true"
                    class="w-full text-nowrap lg:w-auto  rounded-[10px] px-5 h-10! py-0 text-[13px] font-normal me-2">
                    ویرایش
                </Button>

                <Button v-if="!discount?.is_deleted" @click="showInactiveModal = true" :isAdmin="true" variant="outline"
                    class=" w-full text-nowrap lg:w-auto border-2 border-[#0000001f]! rounded-[10px] px-5 h-10! py-0 text-[13px] me-2">
                    غیرفعال کردن
                </Button>

                <Button v-if="discount?.is_deleted" @click="showActiveModal = true" :isAdmin="true" variant="outline"
                    class=" w-full text-nowrap lg:w-auto border-2  rounded-[10px] px-5 h-10! py-0 text-[13px] me-2">
                    فعال کردن
                </Button>


                <Button @click="goToDiscountDetails" :isAdmin="true" variant="outline"
                    class=" w-full text-nowrap lg:w-auto border-2  rounded-[10px] px-5 h-10! py-0 text-[13px] me-2">
                    مشاهده کاربران
                </Button>
            </div>
        </div>

        <div class="flex flex-col gap-6  text-black text-sm font-medium">
            <!-- Header -->
            <div class="w-full lg:w-[45%] flex items-center justify-center gap-2 lg:justify-between">
                <div
                    class="w-full lg:w-fit bg-[#eaeef5] text-[#2d5396] flex items-center justify-center text-[13px] font-medium h-10 rounded-[10px] px-6">
                    شماره تخفیف
                    <span class="font-fa ms-1">{{ discount?.id }}</span>
                </div>
                <div v-if="discount?.type?.titleFa === 'سیستمی'"
                    class="w-full lg:w-fit bg-[#eaeef5] text-[#2d5396] flex items-center justify-center text-[13px] font-medium h-10 rounded-[10px] px-5">
                    <span>{{ discount?.type?.titleFa === 'سیستمی' ? discount?.type?.titleFa : '' }}</span>
                </div>
            </div>
            <!-- Body -->
            <div class="flex flex-col gap-2.5 text-sm text-black font-medium">
                <div class="flex items-center gap-1">
                    <span class="font-fa">{{ discount?.title }}</span>
                </div>
                <div class="flex items-center gap-1">
                    <span>نوع تخفیف:</span>
                    <span>{{ discount?.type?.titleFa }}</span>
                </div>
                <div class="flex items-center gap-1">
                    <span>مقدار تخفیف(%):</span>
                    <span class="font-fa">{{ discount?.rate }}</span>
                </div>
                <div class="flex items-center gap-1">
                    <span>سقف حجمی تخفیف:</span>
                    <span class="font-fa">{{ numberWithCommas(discount?.volume_limit) }}</span>
                </div>
                <div class="flex items-center gap-1">
                    <span>نوع اعتبار:</span>
                    <span>{{
                        discount?.credit_type === 'ONE_TIME_USE' ? ' یک‌بار مصرف' : 'مدت‌دار' }}</span>
                </div>
                <div v-if="discount?.expiry_date" class="flex items-center gap-1">
                    <span>تاریخ اعتبار:</span>
                    <span class="font-fa">{{
                        formatDate(discount?.expiry_date) }}</span>
                </div>
                <div class="flex items-center gap-1">
                    <span>نوع ارز:</span>
                    <span>
                        {{ Array.isArray(discount?.currency) ? 'همه ارزها' : discount?.currency?.titleFa }}
                        <!-- {{ discount?.currency?.length ? 'همه ارزها' : discount?.currency?.titleFa }} -->
                    </span>
                </div>

            </div>
        </div>
    </div>

</template>
